/**
 * AURELIA — component-announcement-bar.js
 * <announcement-bar> — accessible rotating announcements.
 * <localization-selector> — auto-submitting country/language selects.
 *
 * Accessibility notes
 * -------------------
 * • Rotation is auto-playing content, so WCAG 2.2.2 requires a pause control.
 *   One is rendered whenever rotation is enabled and more than one message
 *   exists.
 * • The list is NOT a live region. Announcing every rotation would interrupt a
 *   screen reader user continuously; inactive items are `inert` + aria-hidden
 *   so only the visible message is in the accessibility tree.
 * • Rotation stops on hover, on focus within the bar, when the tab is hidden,
 *   and entirely for `prefers-reduced-motion`.
 */
(function () {
  'use strict';

  const { Element, motion } = window.Aurelia;

  class AnnouncementBar extends Element {
    mount() {
      this.items = this.$$('[data-announcement-item]');
      if (this.items.length === 0) return;

      this.current = 0;
      this.paused = false;
      this.interval = Number(this.dataset.interval) || 6000;
      this.shouldRotate =
        this.dataset.autorotate === 'true' && this.items.length > 1 && !motion.reduced;

      this.setActive(0, { animate: false });

      const previous = this.$('[data-announcement-prev]');
      const next = this.$('[data-announcement-next]');
      this.pauseButton = this.$('[data-announcement-pause]');

      if (previous) this.listen(previous, 'click', () => this.go(-1, { manual: true }));
      if (next) this.listen(next, 'click', () => this.go(1, { manual: true }));

      if (this.pauseButton) {
        this.listen(this.pauseButton, 'click', () => this.togglePause());
      }

      if (!this.shouldRotate) return;

      this.listen(this, 'pointerenter', () => this.suspend());
      this.listen(this, 'pointerleave', () => this.resume());
      this.listen(this, 'focusin', () => this.suspend());
      this.listen(this, 'focusout', () => this.resume());
      this.listen(document, 'visibilitychange', () => {
        if (document.hidden) this.suspend();
        else this.resume();
      });

      this.start();
    }

    unmount() {
      this.stop();
    }

    start() {
      this.stop();
      if (!this.shouldRotate || this.paused) return;
      this.timer = setInterval(() => this.go(1), this.interval);
    }

    stop() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    /** Temporary halt (hover/focus) — does not change the pause button state. */
    suspend() {
      this.stop();
    }

    resume() {
      if (this.paused) return;
      // Do not restart while focus is still inside the bar.
      if (this.contains(document.activeElement) && document.activeElement !== document.body) return;
      this.start();
    }

    togglePause() {
      this.paused = !this.paused;
      this.pauseButton.setAttribute('aria-pressed', String(this.paused));

      const label = this.pauseButton.querySelector('[data-pause-label]');
      if (label && this.pauseButton.dataset.labelPlay && this.pauseButton.dataset.labelPause) {
        label.textContent = this.paused
          ? this.pauseButton.dataset.labelPlay
          : this.pauseButton.dataset.labelPause;
      }

      if (this.paused) this.stop();
      else this.start();
    }

    go(direction, { manual = false } = {}) {
      const total = this.items.length;
      const next = (this.current + direction + total) % total;
      this.setActive(next);

      // A manual interaction restarts the dwell time so the message the
      // visitor asked for is not swapped out a moment later.
      if (manual && !this.paused) this.start();
    }

    setActive(index, { animate = true } = {}) {
      this.items.forEach((item, position) => {
        const isActive = position === index;
        const wasActive = position === this.current;

        item.classList.toggle('is-active', isActive);
        item.classList.toggle('is-leaving', animate && wasActive && !isActive);

        // `inert` removes the item from the tab order and the accessibility
        // tree in one attribute; aria-hidden covers browsers without it.
        item.toggleAttribute('inert', !isActive);
        if (isActive) item.removeAttribute('aria-hidden');
        else item.setAttribute('aria-hidden', 'true');
      });

      this.current = index;
    }
  }

  window.Aurelia.define('announcement-bar', AnnouncementBar);

  /* ---------------------------------------------------------------------
     Localization
     --------------------------------------------------------------------- */
  class LocalizationSelector extends Element {
    mount() {
      this.select = this.$('[data-localization-select]');
      if (!this.select) return;

      this.form = this.closest('form');
      if (!this.form) return;

      this.listen(this.select, 'change', () => {
        // Never disable the select before submitting: a disabled control is
        // excluded from the form data, so the chosen country would be dropped.
        // `aria-busy` plus pointer-events is enough to prevent a double submit.
        this.select.setAttribute('aria-busy', 'true');
        this.style.pointerEvents = 'none';
        this.form.submit();
      });
    }
  }

  window.Aurelia.define('localization-selector', LocalizationSelector);
})();
